import dayjs from "dayjs";
import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import BasePage from "../core/base.page";
import makeStyles from "@mui/styles/makeStyles";
import createStyles from "@mui/styles/createStyles";
import { useNavigate, useParams } from "react-router-dom";
import Typography from "@mui/material/Typography";
import { useAuth } from "../core/context/auth.context";
import UserDebtsService from "../core/service/user-debts.service";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Skeleton } from "@mui/material";
import useScreenSize from "../core/hooks/use-screen-size";
import useUserOs from "../core/hooks/use-user-os";
import AccumulatedDebtInterface from "@homegame/common/dist/interface/accumulated-debt.interface";
import formatCurrency from "../core/helpers/format-currency.helper";
import {
  DebtHistory,
  DebtItemComponent,
} from "./components/debt-item.component";
import TanstackTableComponent from "../core/components/tanstack-table.component";
import { ColumnDef } from "@tanstack/react-table";

const useStyles = makeStyles(() =>
  createStyles({
    table: {},
    container: {
      maxWidth: 500,
      margin: "0 auto",
      height: "100%",
      alignContent: "flex-start",
      paddingTop: 10,
      "&:after": {
        backgroundImage: "url(/cards.svg)",
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "70%",
        content: '""',
        display: "block",
        position: "absolute",
        width: "100%",
        height: "100%",
        filter: "opacity(0.1)",
        top: 0,
        left: 0,
        zIndex: -1,
      },
    },
    headingButtons: {
      margin: "0 auto",
      zIndex: 1,
      position: "fixed",
      top: 0,
      left: 0,
      right: 0,
      display: "flex",
      justifyContent: "space-between",
      maxWidth: 500,
      width: "100%",
    },
    heading: {
      fontWeight: 500,
      fontSize: 19,
      paddingLeft: 40,
      paddingRight: 40,
      textAlign: "center",
      position: "relative",
      paddingBottom: 7,
    },
    wrap: {
      minWidth: "100%",
      paddingLeft: 0,
      height: "100%",
      "& .MuiDataGrid-footerContainer": {
        border: "none",
      },
      "& .MuiDataGrid-cell": {
        border: "none",
        padding: 0,
        minWidth: 200,
      },
      "& .MuiDataGrid-columnHeaders": {
        border: "none",
      },
      "& .MuiDataGrid-root ": {
        border: "none",
      },
    },
  }),
);

export default function DebtHistoryPage() {
  const classes = useStyles({});
  const navigate = useNavigate();

  const { tableId, fromUserId, toUserId, debtType } = useParams();

  const { user } = useAuth();

  const userDebtsService = new UserDebtsService();

  const whereOpts = {
    viewerId: Number(debtType === "my-debts" ? fromUserId : toUserId),
    peerId: Number(debtType === "my-debts" ? toUserId : fromUserId),
    tableId: Number(tableId),
  };

  const [accumulatedDebt, setAccumulatedDebt] = useState<
    AccumulatedDebtInterface | undefined
  >();
  const screenSize = useScreenSize();
  const { isIOS } = useUserOs();

  const innerContainerSize = screenSize.height - (isIOS ? 270 : 255); // magic numbers

  useEffect(() => {
    userDebtsService
      .list({
        fromUserId: Number(fromUserId),
        toUserId: Number(toUserId),
        tableId: Number(tableId),
      })
      .then((res) => {
        if (Array.isArray(res) && Array.isArray(res[0])) {
          setAccumulatedDebt(res[0][0]);
        }
      });
  }, []);

  const totalBalanceString = () => {
    if (!accumulatedDebt) return <></>;
    const isCreditorView = accumulatedDebt.toUserId === user!.id;
    const amount = accumulatedDebt.accumulatedDebt;
    const isPositive = amount > 0;
    const isZero = amount === 0;

    let color = isZero
      ? "#47528c"
      : isCreditorView === isPositive
        ? "success.main"
        : "error";
    let sign = isZero ? "" : isCreditorView === isPositive ? "+" : "-";

    return (
      <Typography color={color} variant="body1" display="inline">
        {sign}
        {formatCurrency(Math.abs(amount), accumulatedDebt.currency)}
      </Typography>
    );
  };

  const columns: ColumnDef<DebtHistory>[] = [
    {
      accessorKey: "amount",
      header: "Amount",
      cell: (info) => {
        const transaction: any = info.row.original;
        return <DebtItemComponent item={transaction} />;
      },
    },
  ];

  return (
    <BasePage>
      <Grid container className={classes.container}>
        <Grid xs={12}>
          <div className={classes.headingButtons}>
            <IconButton
              style={{ padding: 9 }}
              onClick={() => navigate(-1)}
              aria-label="back"
              size="large"
            >
              <ArrowBackIcon />
            </IconButton>
          </div>
        </Grid>

        <Grid item xs={12} className={classes.wrap}>
          <Typography
            className={classes.heading}
            variant="h5"
            gutterBottom
            style={{ marginTop: 15, position: "relative" }}
          >
            {!accumulatedDebt && (
              <Skeleton variant="text" sx={{ fontSize: "1.6rem" }} />
            )}
            {!!accumulatedDebt && (
              <>
                {debtType === "my-debts" &&
                  `Me/${accumulatedDebt.toUser?.name} balance`}
                {debtType === "owed-to-me" &&
                  `${accumulatedDebt.fromUser?.name}/Me balance`}{" "}
                ({totalBalanceString()})
                <br />
                <small style={{ color: "white" }}>
                  In {accumulatedDebt.table.name}
                </small>
              </>
            )}
          </Typography>

          <TanstackTableComponent<DebtHistory>
            columns={columns}
            listMethod={userDebtsService.getTestHistory.bind(userDebtsService)}
            filters={whereOpts}
            height={innerContainerSize}
            className={classes.table}
            getRowId={(row: any) => row.id!}
            hideHeader
            groupByDateKey="createdAt"
            disableAutoGroupByDate={false}
            formatGroupLabel={(rawDate) =>
              dayjs(rawDate).format("MMMM DD, YYYY")
            }
          />
        </Grid>
      </Grid>
    </BasePage>
  );
}
